import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  KeyRound,
  Plus,
  Trash2,
  Edit2,
  MapPin,
  Building,
  Home,
  Star,
  X,
} from 'lucide-react';
import { updateProfileSchema, type UpdateProfileInput } from '@rhpc/shared';
import { api, ApiError } from '@/lib/api';
import { userKeys } from '@/lib/queryKeys';
import { Button, Input, Card } from '@rhpc/ui';
import { PageHeader, PageShell } from '@/components/PageShell';
import { ROLE_LABEL } from '@/lib/roleLabels';
import { LocationAutocomplete } from '@/components/claims/LocationAutocomplete';
import {
  useUserLocations,
  useCreateUserLocation,
  useUpdateUserLocation,
  useDeleteUserLocation,
  type UserLocation,
} from '@/hooks/useUserLocations';

interface ProfileUser {
  id: string;
  email: string;
  name: string;
  role: string;
  department: string | null;
  homeAddress: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function initialsOf(name: string) {
  return name
    .split(' ')
    .map((p) => p.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

function LocationTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'HOME':
      return <Home className="h-4 w-4 text-info" />;
    case 'OFFICE':
      return <Building className="h-4 w-4 text-primary-text" />;
    default:
      return <MapPin className="h-4 w-4 text-text-2" />;
  }
}

function LocationTypeBadge({ type }: { type: string }) {
  switch (type) {
    case 'HOME':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-info-soft px-2 py-0.5 text-xs font-semibold text-info  ">
          Home
        </span>
      );
    case 'OFFICE':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-xs font-semibold text-primary-text  ">
          Office
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold text-text-3  ">
          Other
        </span>
      );
  }
}

export function ProfilePage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState<string | null>(
    () => (location.state as { flash?: string } | null)?.flash ?? null,
  );
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(t);
  }, [successMessage]);
  const [serverError, setServerError] = useState<string | null>(null);

  // Location Form State
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<UserLocation | null>(null);
  const [locLabel, setLocLabel] = useState('');
  const [locAddress, setLocAddress] = useState('');
  const [locType, setLocType] = useState<'HOME' | 'OFFICE' | 'OTHER'>('HOME');
  const [locIsDefault, setLocIsDefault] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);

  const { data: profile, isLoading, error: fetchError } = useQuery({
    queryKey: userKeys.me(),
    queryFn: () => api.get<{ user: ProfileUser }>('/profile'),
  });

  const { data: locationsData, isLoading: isLoadingLocations } = useUserLocations();
  const createLocationMutation = useCreateUserLocation();
  const updateLocationMutation = useUpdateUserLocation();
  const deleteLocationMutation = useDeleteUserLocation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    mode: 'onBlur',
    values: profile
      ? {
          name: profile.user.name,
          homeAddress: profile.user.homeAddress ?? '',
          department: profile.user.department ?? '',
        }
      : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProfileInput) =>
      api.patch<{ user: ProfileUser; message: string }>('/profile', data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
      queryClient.invalidateQueries({ queryKey: userKeys.locations() });
      setSuccessMessage(result.message ?? 'Profile updated successfully');
      setServerError(null);
      setTimeout(() => setSuccessMessage(null), 4000);
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : 'Failed to update profile. Please try again.');
      setSuccessMessage(null);
    },
  });

  const onSubmit = (data: UpdateProfileInput) => {
    updateMutation.mutate(data);
  };

  const openAddLocation = () => {
    setEditingLocation(null);
    setLocLabel('');
    setLocAddress('');
    setLocType('HOME');
    setLocIsDefault(false);
    setLocError(null);
    setIsLocationModalOpen(true);
  };

  const openEditLocation = (loc: UserLocation) => {
    setEditingLocation(loc);
    setLocLabel(loc.label);
    setLocAddress(loc.address);
    setLocType(loc.locationType);
    setLocIsDefault(loc.isDefault);
    setLocError(null);
    setIsLocationModalOpen(true);
  };

  const handleSaveLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locLabel.trim()) {
      setLocError('Location label is required (e.g. "Home (Shah Alam)").');
      return;
    }
    if (!locAddress.trim()) {
      setLocError('Address is required.');
      return;
    }

    try {
      if (editingLocation) {
        await updateLocationMutation.mutateAsync({
          id: editingLocation.id,
          data: {
            label: locLabel.trim(),
            address: locAddress.trim(),
            locationType: locType,
            isDefault: locIsDefault,
          },
        });
        setSuccessMessage('Location updated successfully');
      } else {
        await createLocationMutation.mutateAsync({
          label: locLabel.trim(),
          address: locAddress.trim(),
          locationType: locType,
          isDefault: locIsDefault,
        });
        setSuccessMessage('Location added successfully');
      }
      setIsLocationModalOpen(false);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setLocError(err instanceof ApiError ? err.message : 'Failed to save location.');
    }
  };

  const handleMakeDefault = async (loc: UserLocation) => {
    try {
      await updateLocationMutation.mutateAsync({
        id: loc.id,
        data: { isDefault: true },
      });
      setSuccessMessage(`Set "${loc.label}" as default ${loc.locationType.toLowerCase()} location`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'Failed to set default location.');
    }
  };

  const handleDeleteLocation = async (id: string, label: string) => {
    if (!confirm(`Are you sure you want to delete "${label}"?`)) return;
    try {
      await deleteLocationMutation.mutateAsync(id);
      setSuccessMessage(`Location "${label}" deleted`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'Failed to delete location.');
    }
  };

  if (isLoading) {
    return (
      <PageShell>
        <div className="mb-6">
          <div className="skeleton h-7 w-48 rounded-md" />
          <div className="skeleton mt-2 h-4 w-72 rounded" />
        </div>
        <Card className="mb-6 flex items-center gap-4 p-6">
          <div className="skeleton h-14 w-14 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-40 rounded" />
            <div className="skeleton h-3 w-56 rounded" />
          </div>
        </Card>
        <Card className="space-y-6 p-[28px_32px]">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="skeleton h-4 w-24 rounded" />
              <div className="skeleton h-10 w-full rounded-md" />
            </div>
          ))}
        </Card>
      </PageShell>
    );
  }

  if (fetchError) {
    return (
      <PageShell>
        <PageHeader title="My Profile" />
        <div className="rounded-md border border-danger/20 bg-danger-soft p-4" role="alert">
          <p className="text-sm text-danger">Failed to load profile. Please try refreshing the page.</p>
        </div>
      </PageShell>
    );
  }

  const user = profile?.user;
  if (!user) return null;

  const locations = locationsData?.locations ?? [];

  return (
    <PageShell>
      <PageHeader
        title="My Profile"
        description="View and update your personal information and saved mileage bases."
      />

      {successMessage && (
        <div className="mb-6 flex items-center gap-2 rounded-md border border-ok/20 bg-ok-soft px-3 py-2.5" role="status">
          <Check className="h-4 w-4 text-ok" strokeWidth={2.4} />
          <p className="text-sm text-ok">{successMessage}</p>
        </div>
      )}

      {serverError && (
        <div className="mb-6 rounded-md border border-danger/20 bg-danger-soft p-3" role="alert">
          <p className="text-sm text-danger">{serverError}</p>
        </div>
      )}

      {/* Identity header card */}
      <Card className="mb-6 flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:p-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-white sm:h-14 sm:w-14 sm:text-lg">
            {initialsOf(user.name)}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-base font-bold sm:text-lg">{user.name}</span>
              <span className="inline-flex items-center gap-[5px] rounded-full bg-surface-2 px-2.5 py-[2px] text-[11px] font-semibold text-text-3">
                {ROLE_LABEL[user.role] ?? user.role}
              </span>
            </div>
            <p className="mt-0.5 truncate text-sm text-text-2">{user.email}</p>
          </div>
        </div>
        <div className="flex gap-4 border-t border-border pt-3 text-[13px] text-text-2 sm:shrink-0 sm:flex-col sm:gap-0 sm:border-t-0 sm:pt-0 sm:text-right">
          <div>
            Created <span className="font-mono text-text">{formatDate(user.createdAt)}</span>
          </div>
          <div className="sm:mt-0.5">
            Updated <span className="font-mono text-text">{formatDate(user.updatedAt)}</span>
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        {/* Security Card */}
        <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-[20px_32px]">
          <div>
            <h2 className="text-[15px] font-semibold">Password</h2>
            <p className="mt-0.5 text-[13px] text-text-2">Change the password you use to sign in.</p>
          </div>
          <Link
            to="/change-password"
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-border bg-surface px-[18px] text-sm font-semibold text-text transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <KeyRound className="h-4 w-4" strokeWidth={2} />
            Change password
          </Link>
        </Card>

        {/* Personal Details Card */}
        <Card className="p-4 sm:p-[28px_32px]">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
            <div>
              <h2 className="text-[15px] font-semibold">Personal Details</h2>
              <p className="mt-0.5 text-[13px] text-text-2">
                Email and role are managed by your administrator.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Input
                id="name"
                label="Full Name"
                type="text"
                required
                error={errors.name?.message}
                {...register('name')}
              />
              <Input
                id="department"
                label="Department"
                type="text"
                placeholder="e.g. Engineering, Finance, Operations"
                error={errors.department?.message}
                {...register('department')}
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" loading={isSubmitting || updateMutation.isPending} disabled={!isDirty}>
                {updateMutation.isPending ? 'Saving...' : 'Save Personal Details'}
              </Button>
              {isDirty && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    reset();
                    setServerError(null);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </Card>

        {/* Saved Locations Card (Issue #7: Multiple Home/Office Locations) */}
        <Card className="p-4 sm:p-[28px_32px]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-[15px] font-semibold">Saved Locations</h2>
              <p className="mt-0.5 text-[13px] text-text-2">
                Manage your home, office, and branch bases used for automated mileage deductions.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openAddLocation}
              className="inline-flex items-center gap-1.5 self-start sm:self-auto"
            >
              <Plus className="h-4 w-4" />
              Add Location
            </Button>
          </div>

          <div className="mt-6 space-y-3">
            {isLoadingLocations ? (
              <div className="space-y-3">
                <div className="skeleton h-16 w-full rounded-lg" />
                <div className="skeleton h-16 w-full rounded-lg" />
              </div>
            ) : locations.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-8 text-center">
                <MapPin className="mb-2 h-8 w-8 text-text-2/60" />
                <p className="text-sm font-medium text-text">No saved locations</p>
                <p className="mt-1 text-xs text-text-2 max-w-sm">
                  Add your primary home address or regional branch to calculate mileage deductions automatically.
                </p>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={openAddLocation}
                  className="mt-4 inline-flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add First Location
                </Button>
              </div>
            ) : (
              locations.map((loc) => (
                <div
                  key={loc.id}
                  className="flex flex-col justify-between gap-3 rounded-lg border border-border/80 bg-surface p-4 transition-all hover:border-border sm:flex-row sm:items-center"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-2/60">
                      <LocationTypeIcon type={loc.locationType} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm text-text">{loc.label}</span>
                        <LocationTypeBadge type={loc.locationType} />
                        {loc.isDefault && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-ok-soft px-2 py-0.5 text-xs font-semibold text-ok  ">
                            <Star className="h-3 w-3 fill-ok text-ok  " />
                            Default
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-text-2 break-words">{loc.address}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 self-end sm:self-auto">
                    {!loc.isDefault && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-text-2 hover:text-text"
                        onClick={() => handleMakeDefault(loc)}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-text-2 hover:text-text"
                      onClick={() => openEditLocation(loc)}
                      title="Edit location"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-text-2 hover:text-danger"
                      onClick={() => handleDeleteLocation(loc.id, loc.label)}
                      title="Delete location"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Add / Edit Location Modal */}
      {isLocationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-xl border border-border bg-surface p-6 shadow-xl animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <h3 className="text-base font-semibold">
                {editingLocation ? 'Edit Saved Location' : 'Add New Location'}
              </h3>
              <button
                type="button"
                onClick={() => setIsLocationModalOpen(false)}
                className="rounded-md p-1 text-text-2 hover:bg-surface-2 hover:text-text"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSaveLocation} className="mt-4 space-y-4">
              {locError && (
                <div className="rounded-md border border-danger/20 bg-danger-soft p-3 text-xs text-danger">
                  {locError}
                </div>
              )}

              <div>
                <label htmlFor="locLabel" className="mb-1.5 block text-sm font-medium">
                  Label
                </label>
                <Input
                  id="locLabel"
                  placeholder="e.g. Home (Shah Alam), HQ Office, Subang Branch"
                  value={locLabel}
                  onChange={(e) => setLocLabel(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">Location Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['HOME', 'OFFICE', 'OTHER'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setLocType(type)}
                      className={`flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                        locType === type
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input bg-surface hover:bg-surface-2'
                      }`}
                    >
                      <LocationTypeIcon type={type} />
                      {type === 'HOME' ? 'Home' : type === 'OFFICE' ? 'Office' : 'Other'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="locAddress" className="mb-1.5 block text-sm font-medium">
                  Full Address
                </label>
                <LocationAutocomplete
                  id="locAddress"
                  placeholder="Search address or enter full street address..."
                  value={locAddress}
                  onChange={setLocAddress}
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="locIsDefault"
                  checked={locIsDefault}
                  onChange={(e) => setLocIsDefault(e.target.checked)}
                  className="h-4 w-4 rounded border-input text-primary focus:outline-primary"
                />
                <label htmlFor="locIsDefault" className="text-xs text-text cursor-pointer select-none">
                  Set as default for {locType.toLowerCase()} location
                </label>
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsLocationModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createLocationMutation.isPending || updateLocationMutation.isPending}
                >
                  {editingLocation ? 'Save Changes' : 'Create Location'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageShell>
  );
}
